
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {AccountMarginStable} from '../entity/AccountMarginStable';
import {FieldModelAccountMarginStable} from '../entity/AccountMarginStable';

/**
 * MarginsGetAllResult struct
 */
class MarginsGetAllResult {
  /**
   * Initialize struct
   * @param {!Array=} margins
   * @constructor
   */
  constructor (margins = []) {
    this.Margins = margins
  }

  /**
   * Copy struct (shallow copy)
   * @this {!MarginsGetAllResult}
   * @param {!MarginsGetAllResult} other Other struct
   * @returns {!MarginsGetAllResult} This struct
   */
  copy (other) {
    if (other.Margins != null) {
      this.Margins = []
      for (let item of other.Margins) {
        if (item != null) {
          let tempItem
          tempItem = AccountMarginStable.fromObject(item)
          this.Margins.push(tempItem)
        } else {
          this.Margins.push(null)
        }
      }
    } else {
      this.Margins = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!MarginsGetAllResult}
   * @returns {!MarginsGetAllResult} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new MarginsGetAllResultModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new MarginsGetAllResultModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!MarginsGetAllResult}
   * @param {!MarginsGetAllResult} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof MarginsGetAllResult)) {
      throw new TypeError('Instance of MarginsGetAllResult is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!MarginsGetAllResult}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Margins: ((this.Margins != null) ? Array.from(this.Margins, item => ((item != null) ? item : null)) : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return MarginsGetAllResult.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!MarginsGetAllResult} other Object value
   * @returns {!MarginsGetAllResult} Created struct
   */
  static fromObject (other) {
    return new MarginsGetAllResult().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!MarginsGetAllResult}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return MarginsGetAllResult.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!MarginsGetAllResult}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 10040
  }
}

export { MarginsGetAllResult };

/**
 * Fast Binary Encoding MarginsGetAllResult field model
 */
class FieldModelMarginsGetAllResult extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Margins = new fbe.FieldModelVector(new FieldModelAccountMarginStable(buffer, 4 + 4), buffer, 4 + 4)
  }

  /**
   * Get the field size
   * @this {!FieldModelMarginsGetAllResult}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelMarginsGetAllResult}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Margins.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelMarginsGetAllResult}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Margins.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelMarginsGetAllResult}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelMarginsGetAllResult.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelMarginsGetAllResult}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 10040
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelMarginsGetAllResult}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelMarginsGetAllResult}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Margins.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Margins.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Margins.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelMarginsGetAllResult}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelMarginsGetAllResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelMarginsGetAllResult}
   * @param {!MarginsGetAllResult} fbeValue Default value, defaults is new MarginsGetAllResult()
   * @returns {!MarginsGetAllResult} MarginsGetAllResult value
   */
  get (fbeValue = new MarginsGetAllResult()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelMarginsGetAllResult}
   * @param {!MarginsGetAllResult} fbeValue MarginsGetAllResult value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Margins.fbeSize) <= fbeStructSize) {
      this.Margins.get(fbeValue.Margins)
    } else {
      fbeValue.Margins.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Margins.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelMarginsGetAllResult}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelMarginsGetAllResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelMarginsGetAllResult}
   * @param {!MarginsGetAllResult} fbeValue MarginsGetAllResult value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelMarginsGetAllResult}
   * @param {MarginsGetAllResult} fbeValue MarginsGetAllResult value
   */
  setFields (fbeValue) {
    this.Margins.set(fbeValue.Margins)
  }
}

export { FieldModelMarginsGetAllResult };

/**
 * Fast Binary Encoding MarginsGetAllResult model
 */
class MarginsGetAllResultModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelMarginsGetAllResult(this.buffer, 4)
  }

  /**
   * Get the MarginsGetAllResult model
   * @this {!MarginsGetAllResultModel}
   * @returns {!FieldModelMarginsGetAllResult} model MarginsGetAllResult model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!MarginsGetAllResultModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!MarginsGetAllResultModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return MarginsGetAllResultModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!MarginsGetAllResultModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelMarginsGetAllResult.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!MarginsGetAllResultModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!MarginsGetAllResultModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!MarginsGetAllResultModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!MarginsGetAllResultModel}
   * @param {!MarginsGetAllResult} value MarginsGetAllResult value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!MarginsGetAllResultModel}
   * @param {!MarginsGetAllResult} value MarginsGetAllResult value, defaults is new MarginsGetAllResult()
   * @return {!object} Deserialized MarginsGetAllResult value and its size
   */
  deserialize (value = new MarginsGetAllResult()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new MarginsGetAllResult(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new MarginsGetAllResult(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!MarginsGetAllResultModel}
   * @param {!number} prev Previous MarginsGetAllResult model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { MarginsGetAllResultModel };
